import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { equipmentAPI } from '../../lib/api';
import toast from 'react-hot-toast';

const CATEGORIES = ['bikes', 'ski_gear', 'water_sports', 'hiking', 'camping', 'winter_sports', 'beach_gear', 'photography', 'vehicles', 'other'];

const EquipmentFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basic');

  const [form, setForm] = useState({
    name: '', description: '', category: 'bikes', subCategory: '',
    stock: { total: 1, available: 1 },
    pricing: { hourlyRate: '', dailyRate: '', weeklyRate: '', securityDeposit: 0, currency: 'USD' },
    condition: 'good',
    requirements: { minimumAge: '', licenseRequired: false, depositRequired: true, idRequired: true },
    specifications: [],
    isFeatured: false, isActive: true
  });

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      equipmentAPI.getOne(id)
        .then(res => setForm(res.data.data))
        .catch(() => toast.error('Failed to load equipment'))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const set = (path, value) => {
    setForm(prev => {
      const parts = path.split('.');
      if (parts.length === 1) return { ...prev, [path]: value };
      if (parts.length === 2) return { ...prev, [parts[0]]: { ...prev[parts[0]], [parts[1]]: value } };
      return prev;
    });
  };

  const addSpec = () => setForm(p => ({ ...p, specifications: [...(p.specifications || []), { key: '', value: '' }] }));
  const updateSpec = (i, field, val) => setForm(p => ({
    ...p,
    specifications: p.specifications.map((s, idx) => idx === i ? { ...s, [field]: val } : s)
  }));
  const removeSpec = (i) => setForm(p => ({ ...p, specifications: p.specifications.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await equipmentAPI.update(id, form);
        toast.success('Equipment updated');
        navigate(`/equipment/${id}`);
      } else {
        const res = await equipmentAPI.create(form);
        toast.success('Equipment added');
        navigate(`/equipment/${res.data.data._id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">{isEdit ? 'Edit Equipment' : 'Add Equipment'}</h1>
            <p className="page-subtitle">{isEdit ? 'Update equipment details' : 'Add a new item to your rental catalog'}</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
          {saving ? <><div className="spinner" style={{ borderTopColor: '#fff' }} /> Saving...</> : <><Save size={16} /> {isEdit ? 'Update' : 'Add'} Equipment</>}
        </button>
      </div>

      <div className="tabs">
        {['basic', 'pricing', 'stock', 'specifications'].map(t => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)} style={{ textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>

      <div className="card">
        {tab === 'basic' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="form-grid form-grid-2">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Equipment Name *</label>
                <input className="form-input" placeholder="e.g. Mountain Bike - Trek 820" value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select className="form-select" value={form.category} onChange={e => set('category', e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ').replace(/\b\w/g, x => x.toUpperCase())}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Sub Category</label>
                <input className="form-input" placeholder="e.g. Mountain, Road, BMX" value={form.subCategory || ''} onChange={e => set('subCategory', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Condition</label>
                <select className="form-select" value={form.condition} onChange={e => set('condition', e.target.value)}>
                  {['excellent', 'good', 'fair', 'needs_repair'].map(c => <option key={c} value={c} style={{ textTransform: 'capitalize' }}>{c.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Featured</label>
                <select className="form-select" value={form.isFeatured ? 'true' : 'false'} onChange={e => set('isFeatured', e.target.value === 'true')}>
                  <option value="false">No</option>
                  <option value="true">Yes — show prominently</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-textarea" rows={4} placeholder="Describe the equipment, features, included accessories..." value={form.description || ''} onChange={e => set('description', e.target.value)} />
            </div>
            <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Requirements</div>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Minimum Age</label>
                  <input type="number" className="form-input" placeholder="e.g. 16" value={form.requirements?.minimumAge || ''} onChange={e => set('requirements.minimumAge', parseInt(e.target.value) || '')} min={0} />
                </div>
                {[['licenseRequired', 'License Required'], ['depositRequired', 'Deposit Required'], ['idRequired', 'ID Required']].map(([field, label]) => (
                  <div className="form-group" key={field}>
                    <label className="form-label">{label}</label>
                    <select className="form-select" value={form.requirements?.[field] ? 'true' : 'false'} onChange={e => set(`requirements.${field}`, e.target.value === 'true')}>
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'pricing' && (
          <div className="form-grid form-grid-2" style={{ gap: 20 }}>
            {[['pricing.dailyRate', 'Daily Rate *'], ['pricing.hourlyRate', 'Hourly Rate'], ['pricing.weeklyRate', 'Weekly Rate'], ['pricing.securityDeposit', 'Security Deposit']].map(([field, label]) => (
              <div className="form-group" key={field}>
                <label className="form-label">{label}</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }}>$</span>
                  <input type="number" className="form-input" style={{ paddingLeft: 24 }} placeholder="0.00" min={0} step={0.01}
                    value={form[field.split('.')[0]]?.[field.split('.')[1]] || ''}
                    onChange={e => set(field, parseFloat(e.target.value) || 0)} />
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'stock' && (
          <div className="form-grid form-grid-2" style={{ gap: 20 }}>
            <div className="form-group">
              <label className="form-label">Total Stock *</label>
              <input type="number" className="form-input" min={1} value={form.stock?.total || 1}
                onChange={e => set('stock.total', parseInt(e.target.value) || 1)} />
            </div>
            <div className="form-group">
              <label className="form-label">Available Now</label>
              <input type="number" className="form-input" min={0} value={form.stock?.available ?? form.stock?.total ?? 1}
                onChange={e => set('stock.available', parseInt(e.target.value) || 0)} />
            </div>
          </div>
        )}

        {tab === 'specifications' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontSize: 14, color: '#6B7280' }}>Add key-value specifications (e.g. Weight: 12kg)</span>
              <button className="btn btn-sm btn-secondary" onClick={addSpec}>+ Add Spec</button>
            </div>
            {(form.specifications || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF' }}>No specifications added yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {form.specifications.map((spec, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input className="form-input" placeholder="Key (e.g. Weight)" value={spec.key} onChange={e => updateSpec(i, 'key', e.target.value)} style={{ flex: 1 }} />
                    <input className="form-input" placeholder="Value (e.g. 12 kg)" value={spec.value} onChange={e => updateSpec(i, 'value', e.target.value)} style={{ flex: 2 }} />
                    <button className="btn btn-sm btn-ghost" style={{ color: '#EF4444', flexShrink: 0 }} onClick={() => removeSpec(i)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EquipmentFormPage;
